import React, { useState, useRef } from 'react';

const DocumentStepBuilder = ({ step, onChange, errors = {} }) => {
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const handleFieldChange = (field, value) => {
    const updatedStep = {
      ...step,
      [field]: value
    };
    onChange(updatedStep);
  };

  const handleFileSelect = (files) => {
    if (files && files.length > 0) {
      const file = files[0];
      
      // Basic file validation
      const maxSize = 10 * 1024 * 1024; // 10MB
      const allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'text/plain',
        'image/jpeg',
        'image/png'
      ];

      if (file.size > maxSize) {
        swal('Error', 'File size must be less than 10MB', 'error');
        return;
      }

      if (!allowedTypes.includes(file.type)) {
        swal('Error', 'Only PDF, Word, Excel, text, and image files are allowed', 'error');
        return;
      }

      // Store file for upload
      const updatedStep = {
        ...step,
        template_document: file,
        template_document_name: file.name
      };
      onChange(updatedStep);
    }
  };

  const handleFileInputChange = (e) => {
    handleFileSelect(e.target.files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const handleRemoveFile = () => {
    const updatedStep = {
      ...step,
      template_document: null,
      template_document_name: null
    };
    onChange(updatedStep);
    
    // Clear file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const openFileDialog = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const hasTemplateFile = step.template_document || step.template_document_url;
  const fileName = step.template_document_name || (step.template_document_url && 'Current template file');

  return (
    <div className="document-step-builder">
      <div className="form-group">
        <label className="form-label">
          Template Document <span className="text-muted">(Optional)</span>
        </label>
        
        <div
          className={`file-upload-zone border rounded p-4 text-center ${dragOver ? 'border-primary bg-light' : 'border-dashed'} ${hasTemplateFile ? 'border-success' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={openFileDialog}
          style={{ cursor: 'pointer', minHeight: '120px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
        >
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileInputChange}
            accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.jpg,.jpeg,.png"
            style={{ display: 'none' }}
          />
          
          {hasTemplateFile ? (
            <div className="file-selected">
              <i className="fa fa-file-o fa-2x text-success mb-2"></i>
              <div className="font-weight-bold text-success">{fileName}</div>
              <small className="text-muted">Click to replace or drag a new file here</small>
              <div className="mt-2">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveFile();
                  }}
                >
                  <i className="fa fa-times mr-1"></i> Remove
                </button>
                {step.template_document_url && (
                  <a
                    href={step.template_document_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-sm btn-outline-primary ml-2"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <i className="fa fa-download mr-1"></i> Download Current
                  </a>
                )}
              </div>
            </div>
          ) : (
            <div className="file-upload-prompt">
              <i className={`fa fa-cloud-upload fa-2x mb-2 ${dragOver ? 'text-primary' : 'text-muted'}`}></i>
              <div className="font-weight-bold">
                {dragOver ? 'Drop file here' : 'Upload Template Document'}
              </div>
              <small className="text-muted">
                Click to select or drag & drop<br />
                PDF, Word, Excel, text, or image files (max 10MB)
              </small>
            </div>
          )}
        </div>
        
        <small className="form-text text-muted mt-2">
          <i className="fa fa-info-circle mr-1"></i>
          Users will be able to download this template document to help them complete this step.
        </small>
      </div>

      <div className="form-group">
        <label className="form-label">Template Instructions</label>
        <textarea
          className="form-control"
          rows="3"
          value={step.template_instructions || ''}
          onChange={(e) => handleFieldChange('template_instructions', e.target.value)}
          placeholder="Custom message to show with the template download (e.g., 'Please download and complete this form before uploading...')"
        />
        <small className="form-text text-muted">
          This message will appear above the template download button for users.
        </small>
      </div>

      <div className="row">
        <div className="col-md-6">
          <div className="form-group">
            <label className="form-label">File Type Restrictions</label>
            <select
              className="form-control"
              value={step.allowed_file_types || 'any'}
              onChange={(e) => handleFieldChange('allowed_file_types', e.target.value)}
            >
              <option value="any">Any supported file type</option>
              <option value="documents">Documents only (PDF, Word, Excel)</option>
              <option value="images">Images only (JPG, PNG)</option>
              <option value="pdf">PDF only</option>
            </select>
          </div>
        </div>
        <div className="col-md-6">
          <div className="form-group">
            <label className="form-label">Maximum File Size</label>
            <select
              className="form-control"
              value={step.max_file_size || '10'}
              onChange={(e) => handleFieldChange('max_file_size', e.target.value)}
            >
              <option value="1">1 MB</option>
              <option value="5">5 MB</option>
              <option value="10">10 MB</option>
              <option value="25">25 MB</option>
              <option value="50">50 MB</option>
            </select>
          </div>
        </div>
      </div>

      <style>{`
        .file-upload-zone {
          transition: all 0.2s ease-in-out;
        }
        
        .file-upload-zone:hover {
          border-color: #007bff !important;
          background-color: #f8f9fa !important;
        }
        
        .border-dashed {
          border-style: dashed !important;
        }
        
        .file-selected .btn {
          margin: 0 0.25rem;
        }
      `}</style>
    </div>
  );
};

export default DocumentStepBuilder;